import {
  MIN_COOKIE_WINDOW_DAYS,
  MAX_COOKIE_WINDOW_DAYS,
  MIN_RECURRING_MONTHS,
  MAX_RECURRING_MONTHS,
  MAX_EVENTS_PER_PROGRAM,
  MAX_GIFTS_PER_PROGRAM,
} from '@affily/shared/constants';

/**
 * The rules, in words.
 *
 * Derived from the same constants the server validates against, so the text a
 * user reads cannot drift away from the bound that will actually reject them.
 * If MAX_COOKIE_WINDOW_DAYS changes, this copy changes with it.
 */

export const rule = {
  range: (min, max, unit = '') =>
    `Between ${min} and ${max}${unit ? ` ${unit}` : ''}`,

  max: (n, unit = 'characters') => `Up to ${n} ${unit}`,

  /** Money typed by a human, in the program's currency: converted to integer cents on save. */
  money: (example = '49.99') => `An amount with cents, e.g. ${example}`,

  wholeMoney: () => 'Whole cents — fractions of a cent are rejected',

  percent: (min = 0, max = 100) => `A percentage between ${min} and ${max}`,

  url: () => 'A full URL starting with https://',

  publicUrl: () =>
    'A public URL starting with https:// — this is what lets a brand check the numbers themselves',

  countryCode: () => 'Two-letter country code, e.g. US, GB, DE',

  email: () => 'A valid email address, e.g. you@example.com',

  password: (min = 10) => `At least ${min} characters`,

  /** The pattern is enforced server-side; this is the same rule in English. */
  eventKey: () =>
    'Lowercase letters, numbers and underscores, starting with a letter — e.g. purchase, free_trial',

  cookieWindow: () =>
    `Between ${MIN_COOKIE_WINDOW_DAYS} and ${MAX_COOKIE_WINDOW_DAYS} days`,

  recurringCycles: () =>
    `Between ${MIN_RECURRING_MONTHS} and ${MAX_RECURRING_MONTHS} billing cycles — there is no lifetime option`,

  maxEvents: () => `Up to ${MAX_EVENTS_PER_PROGRAM} actions per program`,

  maxGifts: () => `Up to ${MAX_GIFTS_PER_PROGRAM} gifts per program`,

  wholeNumber: () => 'A whole number',

  optional: () => 'Optional',
};

/**
 * Pull a nested error out of the flat, dotted map the server returns.
 *
 * `server/src/middleware/validate.js` already keys issues by path —
 * `platforms.0.followers`, `events.2.key` — so array-item errors have always
 * been available. Forms simply were not reading them.
 */
export function nestedError(fieldErrors, ...path) {
  if (!fieldErrors) return undefined;
  const key = path.join('.');
  return fieldErrors[key] ?? fieldErrors[path[0]];
}
